import React from 'react';
import {
  Button,
  Col,
  Dropdown,
  Input,
  Menu,
  Modal,
  ModalProps,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  CodeOutlined,
  EditOutlined,
  EllipsisOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { v4 as uuid } from 'uuid';
import {
  InfoButton,
  Label,
  Loading,
  ScreenMask,
  SecondaryButton,
  SiteContent,
  WarningButton,
  message,
} from 'components/common';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  COMMON_MSG_DEFAULT_DISPLAY_DURATION,
  LOADING_ICON_SIZE1,
} from 'constants/config';
import { savePatternCode } from 'services/inventory';
import { PatternCodeContainer } from './styles';
import {
  PatternCodeMode,
  PatternModeOptions,
  PatternTypeOptions,
  extractMatchedPatternCode,
  // getPatternRegularExpression,
  //isPatternCodeItemDelimiter,
} from '.';

type Props = ModalProps & {
  mode: string;
  onSaved?: Function;
  patternCode?: StringKAnyVPair;
};

const PatternCodeItemDialog = (props: Props) => {
  const { useState } = React;
  const [codeItemList, setCodeItemList] = useState<StringKAnyVPair[]>([]);
  // eslint-disable-next-line
  const [hoverCodeItemUuid, setHoverCodeItemUuid] = useState<string>();
  const [inited, setInited] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [lockScreen, setLockScreen] = useState(false);
  const [patternCodeMode, setPatternCodeMode] = useState('Edit');
  const [patternEnable, setPatternEnable] = useState(true);
  const [patternName, setPatternName] = useState('');
  const [patternNote, setPatternNote] = useState('');
  const [patternTestResultList, setPatternTestResultList] = useState<any[]>([]);
  const [patternVersion, setPatternVersion] = useState(0);

  const patternCodeTesterInputRef = React.useRef<any>(null);

  const addCodeItem = () => {
    // note is needed
    setCodeItemList([ ...codeItemList, { note: '', productCodePatternItemNum: 0, uuid: uuid(), } ]);
  };

  const adjustCodeItemSequence = (item: StringKAnyVPair, distance: number) => {
    const len = codeItemList.length;
    const idx = codeItemList.findIndex(e => e.uuid === item.uuid);

    if (idx > -1 && idx + distance < len && idx + distance >= 0) {
      const ti = idx + distance;
      let sorted = false;
      //console.log('idx', idx);
      if (distance > 0) {
        codeItemList.splice(ti + 1, 0, item);
        codeItemList.splice(idx, 1);
        sorted = true;
      } else if (distance < 0) {
        codeItemList.splice(idx, 1);
        codeItemList.splice(ti, 0, item);
        sorted = true;
      }

      if (sorted) {
        setCodeItemList([ ...codeItemList ]);
        setIsFormChanged(true);
      }
    }
  };

  const deletePatternItem = (item: StringKAnyVPair) => {
    Modal.confirm({
      title: 'Confirm',
      content: `Do you want to delete this item(${item.valueType}-${item.rule})?`,
      onOk() {
        const idx = codeItemList.findIndex(e => e.uuid === item.uuid);

        if (idx > -1) {
          codeItemList.splice(idx, 1);
          setCodeItemList([ ...codeItemList ]);
          setIsFormChanged(true);
        }
      },
    });
  };

  const dialogFooter = () => {
    return (
      <Row justify="space-between">
        <Button
          disabled={!isPatternCodeTestable()}
          onClick={toggelPatternCodeMode}
        >
          {patternCodeMode === 'Edit' ? <CodeOutlined /> : <EditOutlined />}
          {patternCodeMode === 'Edit' ? 'Test' : 'Edit'}
        </Button>
        <Space>
          <Button
            onClick={onCancel}
          >
            <CloseOutlined />
            Cancel
          </Button>
          <Button
            disabled={!isSavable()}
            onClick={handleSave}
            type="primary"
          >
            <SaveOutlined />
            Save
          </Button>
        </Space>
      </Row>
    );
  };

  const handleSave = async () => {
    const pd: StringKAnyVPair = {
      //ProductCodePatternNum: 3,
      type: 0,
      name: patternName,
      note: patternNote,
      status: patternEnable ? 1 : 0,
      items: [],
    };

    if (props.mode === 'Edit') {
      if (props.patternCode) {
        pd.productCodePatternNum = props.patternCode.productCodePatternNum;
      } else {
        message.error({
          content: 'Not found pattern code number',
          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
        });
        return;
      }
    }

    pd.items = codeItemList;
    console.log('pd', pd);
    setLockScreen(true);
    try {
      const res = await savePatternCode(pd) as any;
      //console.log('r', res);
      if (res && typeof res === 'object' && res.isSuccess) {
        // @ts-ignore
        if (props.onSaved) setTimeout(() => props.onSaved(), 0);
        // @ts-ignore
        if (props.onCancel) props.onCancel();
      } else {
        message.error({
          content: 'Save failed',
          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
        });
      }
    } catch(e) {
      message.error({
        content: `Saved error: ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
      setLockScreen(false);
    };
  };

  // eslint-disable-next-line
  const initializeState = () => {
    if (props.mode === 'Edit') {
      const { patternCode } = props;
      //console.log(props.patternCode, '<-');
      if (patternCode) {
        const items: StringKAnyVPair[] = [];

        if (Array.isArray(patternCode.items)) {
          patternCode.items.forEach(e => {
            const item = { ...e, uuid: uuid() };

            (item.valueMode === undefined) && (item.valueMode = PatternCodeMode.Standalone);
            (item.aggregationRule === undefined) && (item.aggregationRule = '');
            items.push(item);
          });
        }

        setPatternEnable(patternCode.status === 1);
        setPatternName(patternCode.name);
        setPatternNote(patternCode.note);
        setCodeItemList(items);
        setPatternVersion(patternVersion + 1);
      }
    }
  };

  const isAllPatternItemsFilled = () => {
    return codeItemList.every(e => e.valueType && e.rule);
  };

  const isPatternCodeTestable = () => {
    return codeItemList.length > 0 && isAllPatternItemsFilled();
  };

  const isPatternItemAddable = () => {
    let ret = patternCodeMode === 'Edit';

    if (ret && codeItemList.length > 0) {
      ret = isAllPatternItemsFilled();
    }

    return ret;
  };

  const isSavable = () => {
    let ret = isFormChanged;

    if (ret) ret = !!patternName;

    if (ret) {
      ret = codeItemList.length > 0;

      if (ret) ret = isAllPatternItemsFilled();
    }

    return ret;
  };

  const onCancel = (evt: any) => {
    if (props.onCancel) props.onCancel(evt);
  };

  const onMouseEnterPatternCodeItem = (item: StringKAnyVPair) => {
    //console.log('e', item);
    setHoverCodeItemUuid(item.uuid);
  };

  const onMouseLeavePatternCodeItem = (item: StringKAnyVPair) => {
    //console.log('l', item);
    setHoverCodeItemUuid(undefined);
  };

  const onPatternCodeAggregationChange = (evt: any, item: StringKAnyVPair) => {
    const aggregation = evt.target.value.trim();

    //console.log('ag', aggregation);
    item.aggregationRule = aggregation;
    setCodeItemList([ ...codeItemList ]);
    setIsFormChanged(true);
  };

  const onPatternCodeChange = (evt: any, item: StringKAnyVPair) => {
    const code = evt.target.value;

    //console.log('c', code);
    item.rule = code;
    setCodeItemList([ ...codeItemList ]);
    setIsFormChanged(true);
  };

  const onPatternEnableChange = (enable: boolean) => {
    setPatternEnable(enable);
    setIsFormChanged(true);
  };

  const onPatternNameChange = (evt: any) => {
    const name = evt.target.value.trim();

    // console.log('n', name);
    setIsFormChanged(patternName !== name);
    setPatternName(name);
  };

  const onPatternNoteChange = (evt: any) => {
    const note = evt.target.value.trim();

    // console.log('n', note);
    setIsFormChanged(patternNote !== note);
    setPatternNote(note);
  };

  const onPatternTesterInputKeyDown = (evt: any) => {
    const code = evt.target.value.trim();
    //console.log('evt', evt.keyCode, code);
    if (code && [9, 13].indexOf(evt.keyCode) > -1) {
      testPatternCode();
    }
  };

  const onSelectPatternMode = (item: StringKAnyVPair, typ: string) => {
    item.valueMode = typ;
    setCodeItemList([ ...codeItemList ]);
    setIsFormChanged(true);
  };

  const onSelectPatternType = (item: StringKAnyVPair, typ: string) => {
    item.valueType = typ;
    setCodeItemList([ ...codeItemList ]);
    setIsFormChanged(true);
  };

  const patternCodeInputClassName = (item: StringKAnyVPair) => {
    const cls = ['pt-code-input'];

    (item.valueMode === PatternCodeMode.Aggregation) && cls.push('agg-mode');

    return cls.join(' ');
  };

  const patternModeEditor = (item: StringKAnyVPair) => {
    return (
      <>
        {item.valueMode === PatternCodeMode.Aggregation && (
          <Input
            className="agg-input"
            onChange={evt => onPatternCodeAggregationChange(evt, item)}
            placeholder="Input aggregation"
            value={item.aggregationRule}
          />
        )}
        <Select
          className="addon-after"
          onChange={val => onSelectPatternMode(item, val)}
          options={PatternModeOptions}
          value={item.valueMode}
        />
      </>
    );
  };

  const patternTypeSelector = (item: StringKAnyVPair) => {
    return (
      <Select
        defaultValue={item.valueType}
        onChange={val => onSelectPatternType(item, val)}
        options={PatternTypeOptions}
      />
    );
  };

  const renderCodeItemList = () => {
    return codeItemList.map((e, i) => (
      <Row
        align="middle"
        className="pt-item-edit-row"
        key={e.uuid}
        onMouseEnter={() => onMouseEnterPatternCodeItem(e)}
        onMouseLeave={() => onMouseLeavePatternCodeItem(e)}
      >
        <div className="ant-input-group-wrapper w-none ml-6"
          style={{width: 'calc(100% - 104px)'}}
        >
          <Input
            //addonAfter={patternModeEditor(e)}
            addonBefore={patternTypeSelector(e)}
            allowClear
            className={patternCodeInputClassName(e)}
            defaultValue={e.rule}
            onChange={evt => onPatternCodeChange(evt, e)}
            
          />
          {patternModeEditor(e)}
        </div>
        <div className="ant-input-group-wrapper w-none ml-6">
          <SecondaryButton
            disabled={i === 0}
            onClick={() => adjustCodeItemSequence(e, -1)}
            size="small"
          >
            <ArrowUpOutlined />
          </SecondaryButton>
          <WarningButton
            disabled={i === codeItemList.length - 1}
            onClick={() => adjustCodeItemSequence(e, 1)}
            size="small"
          >
            <ArrowDownOutlined />
          </WarningButton>
          <Dropdown
            overlay={(
              <Menu>
                <Menu.Item key="rm-pt-item-btn" onClick={() => deletePatternItem(e)}>
                  <Label className="label-danger">
                    <DeleteOutlined />
                    &nbsp;Delete
                  </Label>
                </Menu.Item>
              </Menu>
            )}
          >
            <Button size="small">
              <EllipsisOutlined />
            </Button>
          </Dropdown>
        </div>
      </Row>
    ));
  };

  // eslint-disable-next-line
  const renderPatternCode = () => {
    //return codeItemList.map((e: any) => isPatternCodeItemDelimiter(e) ? <Tooltip key={`pci-${e.uuid}`} title={e.valueType} visible={e.uuid === hoverCodeItemUuid}><span className="text-info fw-600">{e.rule}</span></Tooltip> : (e.uuid === hoverCodeItemUuid ? <span key={`pci-${e.uuid}`}>(<Tooltip title={e.valueType} visible={true}><span className="bg-daybreak-blue-1">{e.rule}</span></Tooltip>)</span> : <span key={`pci-${e.uuid}`}>({e.rule})</span>));
    return codeItemList.map((e: any) => <Tooltip key={`pci-${e.uuid}`} title={e.valueType}><span className="bg-item-1 br-5 m-0-1 p-0-1-1">{e.rule}</span></Tooltip>);
  };

  const renderPatternTestResult = () => {
    return patternTestResultList.map(e => {
      let ret: any = null;

      switch(e.result) {
        case 0:
        case 1:
          ret = <p className="mb-2" key={uuid()}><span className="text-info">{e.type}</span> =&gt; <span className={e.result === 1 ? 'text-danger' : 'text-blue'}>{e.value}</span></p>;
          break;

        /*case 1:
          ret = <p className="text-danger" key={uuid()}>{e.value}</p>;
          break;*/
      }

      return ret;
    });
  };

  const renderPatternTester = () => {
    return (
      <Row className="mt-6" style={{height: 200}}>
        <Input
          addonAfter={runTestButton()}
          allowClear
          autoFocus
          className="input-addon-btn"
          onKeyDown={onPatternTesterInputKeyDown}
          placeholder="Please input test code here"
          ref={patternCodeTesterInputRef}
        />
        <div className="ant-card ant-card-bordered mt-8 p-8 y-scrollable w-100" style={{ height: 'calc(100% - 40px)' }}>
          {renderPatternTestResult()}
        </div>
      </Row>
    );
  };

  const runTestButton = () => {
    return (
      <Button
        className="h-30 p-2-8"
        onClick={testPatternCode}
        type="primary"
      >
        <CodeOutlined />
      </Button>
    );
  };

  const testPatternCode = () => {
    if (!patternCodeTesterInputRef.current) return;

    const code = patternCodeTesterInputRef.current.input.value.trim();

    if (code) {
      /*const reg = new RegExp(getPatternRegularExpression(codeItemList));
      const res = reg.exec(code);
      //console.log('code', code, reg, res);
      if (Array.isArray(res)) {
        const matchedCodes = extractMatchedPatternCode(res, codeItemList);
        //console.log('l', res.length, matchedCodes);
        setPatternTestResultList(matchedCodes.map(e => ({ ...e, result: 0, })));
      } else {
        setPatternTestResultList([{result: 1, message: 'Not Match'}]);
      }*/
      setPatternTestResultList(extractMatchedPatternCode(codeItemList, code));
    } else {
      setPatternTestResultList([]);
    }
  };

  const toggelPatternCodeMode = () => {
    if (patternCodeMode === 'Test') setPatternTestResultList([]);

    setPatternCodeMode(patternCodeMode === 'Edit' ? 'Test' : 'Edit');
  };

  React.useEffect(() => {
    if (!inited) {
      initializeState();
      setInited(true);
    }
  }, [inited, initializeState]);

  return (<>
    <Modal
      {...props}
      footer={dialogFooter()}
    >
      <SiteContent className="common-content">
        <PatternCodeContainer>
          <Row align="middle">
            <Col className="text-end" span={4}>
              <Label className="label-bold label-required tail-colon2">Name</Label>
            </Col>
            <Col span={18}>
              <Input
                defaultValue={patternName}
                onChange={onPatternNameChange}
                key={`ptn-name${patternVersion}`}
              />
            </Col>
          </Row>
          <Row className="mt-8">
            <Col className="text-end" span={4}>
              <Label className="label-bold tail-colon2">Enable</Label>
            </Col>
            <Col span={18}>
              <Switch
                checkedChildren="Yes"
                defaultChecked={patternEnable}
                key={`ptn-name${patternVersion}`}
                onChange={onPatternEnableChange}
                unCheckedChildren="No"
              />
            </Col>
          </Row>
          <Row className="mt-8">
            <Col className="text-end" span={4}>
              <Label className="label-bold tail-colon2">Note</Label>
            </Col>
            <Col span={18}>
              <Input.TextArea
                defaultValue={patternNote}
                key={`ptn-note${patternVersion}`}
                onChange={onPatternNoteChange}
              />
            </Col>
          </Row>
          <Row align="middle" className="mt-8">
            <Col className="text-end" span={4}>
              <Label className="label-required label-bold tail-colon2">Pattern Code</Label>
            </Col>
            <Col span={19}>
              <Row align="middle">
                <Row style={{ width: 'calc(100% - 32px)', minHeight: 22 }}>
                  {patternCodeMode === 'Test' && isPatternCodeTestable() && renderPatternCode()}
                </Row>
                <InfoButton
                  className="p-4-8"
                  disabled={!isPatternItemAddable()}
                  onClick={addCodeItem}
                >
                  <PlusOutlined />
                </InfoButton>
              </Row>
            </Col>
          </Row>
          {codeItemList.length > 0 && (
            <Row className="mt-2">
              <Col span={4}>
              </Col>
              <Col
                className={ patternCodeMode === 'Edit' ? 'y-scrollable' : undefined }
                span={18}
                style={{ maxHeight: patternCodeMode === 'Edit' ? 206 : undefined }}
              >
                {patternCodeMode === 'Edit' && renderCodeItemList()}
                {patternCodeMode === 'Test' && renderPatternTester()}
              </Col>
            </Row>
          )}
        </PatternCodeContainer>
      </SiteContent>
    </Modal>
    {lockScreen && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default PatternCodeItemDialog;
